import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import { api, downloadBlob, errMsg, fmtDate, fmtNum } from "@/lib/api";
import { Empty, NativeSelect, Spinner } from "@/components/Brand";
import { Confirm } from "@/components/Confirm";
import { PassBadge } from "@/components/ResultTable";
import { GoldBtn, PageTitle } from "@/pages/admin/AdminLayout";
import { Input } from "@/components/ui/input";

export default function ResultsAdmin() {
  const [cats, setCats] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [f, setF] = useState({ category_id: "", package_id: "", q: "", passed: "" });
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/categories", { params: { all: true } }).then((r) => setCats(r.data));
    api.get("/admin/packages").then((r) => setPkgs(r.data));
  }, []);
  const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
  const load = useCallback(() => {
    api.get("/admin/results", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(f)]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const del = async (id) => {
    try {
      await api.delete(`/admin/results/${id}`);
      toast.success("Hasil dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const exportCsv = () => downloadBlob("/admin/results/export", "hasil-ujian-adikara.csv", params).catch((e) => toast.error(errMsg(e)));

  return (
    <div>
      <PageTitle title="Hasil Ujian" desc="Rincian nilai per peserta dan per materi.">
        <GoldBtn onClick={exportCsv} data-testid="results-export-btn"><Download className="h-4 w-4" />Ekspor CSV</GoldBtn>
      </PageTitle>
      <div className="mb-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <div className="relative"><Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" /><Input className="pl-9" placeholder="Nama/email peserta" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="results-search-input" /></div>
        <NativeSelect value={f.category_id} onChange={(e) => setF({ ...f, category_id: e.target.value, package_id: "" })} data-testid="results-filter-category"><option value="">Semua kategori</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</NativeSelect>
        <NativeSelect value={f.package_id} onChange={(e) => setF({ ...f, package_id: e.target.value })} data-testid="results-filter-package"><option value="">Semua paket</option>{pkgs.filter((p) => !f.category_id || p.category_id === f.category_id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</NativeSelect>
        <NativeSelect value={f.passed} onChange={(e) => setF({ ...f, passed: e.target.value })} data-testid="results-filter-passed"><option value="">Semua status</option><option value="true">Lulus</option><option value="false">Tidak lulus</option></NativeSelect>
      </div>
      {!rows ? <Spinner /> : rows.length === 0 ? <Empty text="Belum ada hasil ujian." /> : (
        <div className="overflow-x-auto rounded-xl border bg-white">
          <table className="w-full min-w-[900px] text-sm" data-testid="results-table">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Peserta</th><th>Paket</th><th>Selesai</th><th>Skor per materi</th><th>Total</th><th>Status</th><th className="pr-4 text-right">Aksi</th></tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id} className="border-t align-top" data-testid={`result-admin-row-${r.id}`}>
                <td className="px-4 py-3 font-semibold">{r.user_name}<div className="text-xs font-normal text-slate-500">{r.user_email}</div></td>
                <td>{r.package_name}</td><td>{fmtDate(r.finished_at)}</td>
                <td className="py-3 text-xs">{r.subjects.map((s) => <div key={s.subject_id}>{s.name}: <b>{fmtNum(s.score)}</b>/{fmtNum(s.max_score)}</div>)}</td>
                <td className="py-3 font-mono font-bold">{fmtNum(r.total)}/{fmtNum(r.max_total)}</td>
                <td className="py-3"><PassBadge passed={r.passed} /></td>
                <td className="py-3 pr-4 text-right">
                  <Link to={`/hasil/${r.id}`} className="mr-3 text-xs font-bold text-amber-700" data-testid={`result-admin-view-${r.id}`}>Detail</Link>
                  <Confirm testid={`result-delete-${r.id}`} title="Hapus hasil ujian?" description="Hasil ini akan dihapus permanen dan peserta dapat mengulang jika batas percobaan mengizinkan." onConfirm={() => del(r.id)}
                    trigger={<button data-testid={`result-delete-btn-${r.id}`}><Trash2 className="inline h-4 w-4 text-red-600" /></button>} />
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
